/*
 * GALPÓN DE DISEÑO — Motor client-side de la PDP
 * ---------------------------------------------------------------------------
 * Mismo principio que el motor de colección (gd-collection-grid.js): la familia
 * completa (synaboard.family) viene precargada en el DOM; el JS hace SWAP in-place
 * sin fetch. Los hermanos son Products separados (NO variantes Shopify) — cada uno
 * tiene su panel de galería precargado + un blob JSON con su texto.
 *
 * Hace:
 *   1. Swap de versión al click en pill: galería + título + precio + stock +
 *      descripción + specs + el input de variante del form + la URL. Sin recargar.
 *   2. Miniaturas dentro de cada panel de galería.
 *   3. Zoom propio (lightbox sobrio) — reemplaza el overlay nativo de Horizon.
 *
 * Carga: defer desde theme.liquid, solo en template product.
 */
(function () {
  'use strict';

  const pdp = document.querySelector('[data-gd-pdp]');
  const gallery = document.querySelector('gd-pdp-gallery');
  if (!pdp && !gallery) return;

  // --- Datos de hermanos: { id -> {title, url, variantId, price, available, ...} }
  const siblingData = {};
  document.querySelectorAll('[data-gd-sibling]').forEach((tag) => {
    try {
      siblingData[tag.getAttribute('data-gd-sibling')] = JSON.parse(tag.textContent);
    } catch (e) {
      /* blob malformado: lo ignoramos, el swap caerá al fallback de navegar */
    }
  });

  // --- Campos de texto que cambian en el swap (data-gd-field) ----------------
  const fields = {};
  document.querySelectorAll('[data-gd-field]').forEach((el) => {
    fields[el.getAttribute('data-gd-field')] = el;
  });

  // --- Input de variante del form de Horizon (para que Agregar al carro pegue)
  function variantInput() {
    return document.querySelector('product-form-component [ref="variantId"], product-form-component input[name="id"]');
  }

  // =========================================================================
  // 1. SWAP DE VERSIÓN
  // =========================================================================
  function swapTo(id) {
    const data = siblingData[id];
    if (!data) return false;

    // Galería: mostrar el panel del hermano, ocultar el resto.
    if (gallery) {
      gallery.querySelectorAll('.gd-gallery__panel').forEach((panel) => {
        const isTarget = panel.getAttribute('data-gallery-for') === id;
        panel.hidden = !isTarget;
        if (isTarget) {
          // showImageAt(0) resetea el thumb activo Y la foto principal a la primera
          // (resetThumbs solo marcaba el thumb → si habías navegado fotos en este
          // panel antes de cambiar de versión, volvía DESINCRONIZADO: thumb 0 activo
          // con la foto 3 en pantalla).
          showImageAt(panel, 0);
          fadeInPanelImage(panel);
        }
      });
      gallery.setAttribute('data-current-id', id);
    }

    // Texto
    if (fields.title) fields.title.textContent = data.title;
    if (fields['breadcrumb-current']) fields['breadcrumb-current'].textContent = data.title;
    if (fields.subtitle) {
      fields.subtitle.textContent = data.subtitle || '';
      fields.subtitle.hidden = !data.subtitle;
    }
    if (fields.price) fields.price.textContent = data.price;
    if (fields.stock) fields.stock.innerHTML = data.stockHtml || '';
    if (fields.description) fields.description.innerHTML = data.descriptionHtml || '';
    if (fields.specs) fields.specs.innerHTML = data.specsHtml || '';

    // Lista "otras versiones": oculta la fila del producto que ahora es el actual
    // y muestra el resto (el bloque siempre lista las OTRAS, no la que estás viendo).
    document.querySelectorAll('[data-gd-row]').forEach((row) => {
      row.hidden = row.getAttribute('data-gd-row') === id;
    });

    // Form: apuntar a la variante del hermano (cada hermano = otro Product)
    const vi = variantInput();
    if (vi && data.variantId) vi.value = String(data.variantId);

    // Stock único: re-evaluar data-single-stock (oculta/muestra el selector de cantidad)
    const details = document.querySelector('.product-details');
    if (details) {
      if (data.singleStock) {
        details.setAttribute('data-single-stock', '');
      } else {
        details.removeAttribute('data-single-stock');
      }
    }

    // URL + título sin recargar (deep-link correcto, botón atrás del navegador,
    // share/og coherente). El título es product_name + variante.
    if (data.url) history.replaceState({ gdPdp: id }, '', data.url);
    if (data.title) {
      var t = data.title;
      if (data.subtitle) t += ' - ' + data.subtitle;
      document.title = t + ' – Galpón de Diseño';
    }

    pdp.setAttribute('data-current-id', id);

    // Avisar al estimador de despacho (gd-shipping-selector en modo pdp) que la pieza
    // cambió: cada versión es un Product individual con su propio costo de envío. El
    // selector re-cotiza con el mismo destino guardado, sin recargar.
    if (data.variantId) {
      document.dispatchEvent(new CustomEvent('gd:pdp-variant-change', {
        detail: { variantId: data.variantId },
      }));
    }

    // La pieza cambió: el usuario tiene que VER la foto nueva, sino el swap es
    // invisible. Pero nada de scroll incondicional (jank gratis). Premium = la
    // página queda quieta cuando la foto YA se ve, y solo sube cuando de verdad
    // no se ve. Criterio: ¿el tope de la galería quedó por encima del navbar
    // (cortada/empujada fuera de vista)? Si sí, subimos suave; si está a la vista,
    // no tocamos nada.
    //   - Desktop normal (foto y pills lado a lado, ambos visibles) → no se mueve.
    //   - Mobile (foto apilada arriba de los pills) → casi siempre cortada → sube.
    //   - Desktop tras bajar a leer specs → cortada → sube a ver la pieza nueva.
    if (gallery) {
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      // Alto del navbar fixed: 84px desktop / 68px mobile (breakpoint 749px, el
      // mismo que usa galpon-overrides.css para el respiro del breadcrumb). NO usar
      // var(--header-height) — Horizon la setea por JS post-paint y es inestable.
      const HEADER = isMobile ? 68 : 84;
      const RESPIRO = 24;
      // BUG (mobile): medir `gallery` (el <gd-pdp-gallery>) daba top=0 SIEMPRE porque ese
      // elemento es display:contents (sin caja) → el scroll subía solo ~92px (inútil) y la
      // foto seguía fuera de vista. Hay que medir el ELEMENTO VISIBLE de la galería: el
      // carrusel `.gd-gallery__swipe` en mobile, o la foto `.gd-gallery__main` en desktop.
      const panel = gallery.querySelector('.gd-gallery__panel:not([hidden])') || gallery;
      const visualEl = isMobile
        ? (panel.querySelector('.gd-gallery__swipe') || panel.querySelector('.gd-gallery__main'))
        : (panel.querySelector('.gd-gallery__main') || panel.querySelector('.gd-gallery__swipe'));
      if (!visualEl) return true;
      const top = visualEl.getBoundingClientRect().top;
      // En MOBILE las versiones viven DEBAJO de la foto → al tocar una, el usuario casi
      // siempre está scrolleado lejos de la foto (top muy negativo). Subimos para que la
      // pieza nueva quede a la vista. En DESKTOP, foto y versiones están lado a lado: si la
      // foto ya se ve (top >= HEADER), no movemos nada (premium = página quieta).
      // Criterio único: si el tope de la foto está por encima del navbar (cortada/oculta),
      // subir suave hasta dejarla completamente debajo del navbar; si está a la vista, nada.
      if (top < HEADER - 1) {
        const y = window.scrollY + top - HEADER - RESPIRO;
        window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      }
    }
    return true;
  }

  // Escuchamos en document: los thumbnails de hermanos viven en la parte 'after',
  // FUERA del [data-gd-pdp] (a propósito: van debajo del add-to-cart, no compiten).
  document.addEventListener('click', (e) => {
    const sib = e.target.closest('[data-gd-variant]');
    if (!sib || !pdp) return;
    const id = sib.getAttribute('data-gd-variant');
    if (id === pdp.getAttribute('data-current-id')) return; // ya está
    // Si tenemos los datos → swap; si no (familia grande sin precarga) → navegar.
    if (!swapTo(id)) {
      const url = sib.getAttribute('data-gd-url');
      if (url) window.location.href = url;
    } else {
      e.preventDefault();
    }
  });

  // =========================================================================
  // 2. MINIATURAS + FLECHAS (dentro de cada panel)
  // =========================================================================

  // Fade-in de la foto al cambiar de versión: las fotos de los hermanos son
  // loading=lazy → al mostrar el panel recién empiezan a bajar; sin esto la foto
  // "aparece de golpe" cuando llega. A PRUEBA DE FALLOS: si la foto YA está cargada
  // (cacheada / segunda visita), NO hacemos nada (aparece instantánea, sin fade
  // innecesario). Solo si está bajando: la armamos oculta y la revelamos al load.
  // El estado oculto cuelga de .gd-img-fade-armed (que el JS pone) → si el JS no
  // corre, la foto se ve normal, nunca invisible.
  function fadeInPanelImage(panel) {
    const img = panel.querySelector('.gd-gallery__main img');
    if (!img) return;
    if (img.complete && img.naturalWidth > 0) return; // ya cargada → instantánea
    img.classList.add('gd-img-fade-armed');
    img.classList.remove('gd-img-fade-in');
    const reveal = () => {
      img.classList.add('gd-img-fade-in');
      img.removeEventListener('load', reveal);
      img.removeEventListener('error', reveal);
    };
    img.addEventListener('load', reveal, { once: true });
    img.addEventListener('error', reveal, { once: true });
    // red de seguridad: si por lo que sea el load no dispara, revelar igual.
    setTimeout(reveal, 1500);
  }

  // Cambia la foto principal del panel a la del thumbnail idx (con wrap: -1 desde
  // la primera va a la última). Única vía de cambio de foto — la usan los clicks
  // en thumbnails Y las flechas prev/next de desktop, así el estado (thumb activo)
  // nunca se desincroniza.
  function showImageAt(panel, idx) {
    const thumbs = [...panel.querySelectorAll('[data-gd-thumb]')];
    if (!thumbs.length) return;
    const n = thumbs.length;
    const i = ((idx % n) + n) % n; // wrap circular
    const thumb = thumbs[i];
    const mainImg = panel.querySelector('.gd-gallery__main .gd-gallery__img');
    const thumbImg = thumb.querySelector('img');
    if (mainImg && thumbImg) {
      // La miniatura ya tiene la URL; subimos su resolución para el main.
      const big = (thumbImg.currentSrc || thumbImg.src).replace(/width=\d+/, 'width=1400');
      if (mainImg.src !== big) {
        mainImg.src = big;
        if (thumbImg.srcset) mainImg.removeAttribute('srcset');
        // Si la resolución grande viene bajando, fade en vez de pop (mismo contrato
        // a prueba de fallos que el swap de versión: cacheada → instantánea).
        fadeInPanelImage(panel);
      }
    }
    thumbs.forEach((t) => t.classList.toggle('gd-gallery__thumb--active', t === thumb));
  }

  if (gallery) {
    gallery.addEventListener('click', (e) => {
      const thumb = e.target.closest('[data-gd-thumb]');
      if (thumb) {
        showImageAt(thumb.closest('.gd-gallery__panel'), Number(thumb.getAttribute('data-gd-thumb')));
        return;
      }
      // Flechas prev/next de desktop (solo existen con 2+ fotos)
      const nav = e.target.closest('[data-gd-nav]');
      if (nav) {
        const panel = nav.closest('.gd-gallery__panel');
        const thumbs = [...panel.querySelectorAll('[data-gd-thumb]')];
        const current = thumbs.findIndex((t) => t.classList.contains('gd-gallery__thumb--active'));
        showImageAt(panel, (current < 0 ? 0 : current) + Number(nav.getAttribute('data-gd-nav')));
      }
    });
  }

  // =========================================================================
  // 2b. CARRUSEL SWIPEABLE (mobile) — sincronizar los dots con el scroll
  // =========================================================================
  // El track es scroll-snap nativo (CSS); el JS solo refleja la posición en los dots.
  // rAF-throttle para no recalcular en cada píxel de scroll.
  function setupSwipe(panel) {
    const track = panel.querySelector('[data-gd-track]');
    const dotsWrap = panel.querySelector('[data-gd-dots]');
    if (!track || !dotsWrap) return;
    const dots = dotsWrap.querySelectorAll('[data-gd-dot]');
    if (dots.length < 2) return;
    let ticking = false;
    const sync = () => {
      ticking = false;
      const slideW = track.clientWidth || 1;
      const idx = Math.round(track.scrollLeft / slideW);
      dots.forEach((d, i) => d.classList.toggle('gd-gallery__dot--active', i === idx));
    };
    track.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(sync);
    }, { passive: true });
  }
  if (gallery) {
    // armar el panel actual (visible) y cualquiera que se muestre luego (swap)
    gallery.querySelectorAll('.gd-gallery__panel').forEach(setupSwipe);
  }

  // =========================================================================
  // 3. ZOOM propio (lightbox sobrio + SWIPEABLE) — reemplaza el overlay nativo
  // =========================================================================
  // El lightbox arma un track scroll-snap con TODAS las fotos del panel actual, y abre
  // en la foto que se tocó → en el zoom también se puede deslizar entre fotos (igual que
  // la galería). Una sola foto = un solo slide, sin swipe.
  let lightbox = null;
  function ensureLightbox() {
    if (lightbox) return lightbox;
    lightbox = document.createElement('div');
    lightbox.className = 'gd-gallery__lightbox';
    lightbox.setAttribute('role', 'dialog');
    lightbox.setAttribute('aria-modal', 'true');
    lightbox.innerHTML =
      '<button type="button" class="gd-gallery__lightbox-close" aria-label="Cerrar">&times;</button>' +
      '<div class="gd-gallery__lightbox-track" data-gd-lb-track></div>';
    document.body.appendChild(lightbox);
    lightbox.addEventListener('click', (e) => {
      // cerrar al tocar el fondo o la X (NO al tocar una foto)
      if (e.target === lightbox ||
          e.target.closest('.gd-gallery__lightbox-close') ||
          e.target.classList.contains('gd-gallery__lightbox-track')) {
        closeZoom();
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeZoom();
    });
    return lightbox;
  }
  function hiRes(img) {
    return (img.currentSrc || img.src).replace(/width=\d+/, 'width=2000');
  }
  // Abre el lightbox con todas las fotos del panel del trigger, en el índice tocado.
  function openZoomFromTrigger(trigger) {
    const panel = trigger.closest('.gd-gallery__panel') || gallery;
    // fuente de las fotos: los slides del carrusel mobile (todas) o, fallback, la foto main
    let imgs = [...panel.querySelectorAll('.gd-gallery__slide img')];
    if (!imgs.length) {
      const m = panel.querySelector('.gd-gallery__main img');
      if (m) imgs = [m];
    }
    if (!imgs.length) return;
    const startIdx = Math.max(0, imgs.indexOf(trigger.querySelector('img')));
    const lb = ensureLightbox();
    const track = lb.querySelector('[data-gd-lb-track]');
    track.innerHTML = imgs
      .map((im) => '<div class="gd-gallery__lightbox-slide"><img class="gd-gallery__lightbox-img" src="' +
        hiRes(im) + '" alt="' + (im.alt || '').replace(/"/g, '&quot;') + '"></div>')
      .join('');
    lb.classList.add('gd-gallery__lightbox--open');
    document.body.style.overflow = 'hidden';
    // posicionar en la foto tocada (sin animar: salto directo)
    requestAnimationFrame(() => {
      track.scrollLeft = startIdx * track.clientWidth;
    });
  }
  function closeZoom() {
    if (!lightbox) return;
    lightbox.classList.remove('gd-gallery__lightbox--open');
    document.body.style.overflow = '';
  }

  if (gallery) {
    gallery.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-gd-zoom]');
      if (!trigger) return;
      openZoomFromTrigger(trigger);
    });
  }
})();
